import React, { useEffect, useState, useRef, Component } from 'react';
import { ChevronDown, Check, Search, Star, Workflow, Server, Database, Archive } from 'lucide-react';
import { agentRegistry, getAgent, getAvailableAgents } from '../../data/agentRegistry';
import { ChatEndpoint } from '../../services/api';
const agentIconMap: Record<string, ComponentType<{
  size?: number;
  className?: string;
}>> = {
  Workflow,
  Server,
  Database,
  Archive
};
function AgentIcon({
  name,
  size = 16,
  className = ''
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const Icon = agentIconMap[name];
  if (Icon) return <Icon size={size} className={className} data-id="element-1799" />;
  return <span className={className} data-id="element-1800">{name}</span>;
}
interface AgentSelectorProps {
  selectedAgentId: ChatEndpoint;
  onSelect: (id: ChatEndpoint) => void;
}
export function AgentSelector({
  selectedAgentId,
  onSelect
}: AgentSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectedAgent = getAgent(selectedAgentId) || agentRegistry[0];
  const availableAgents = getAvailableAgents();
  const filteredAgents = availableAgents.filter(agent => agent.name.toLowerCase().includes(searchQuery.toLowerCase()) || agent.description.toLowerCase().includes(searchQuery.toLowerCase()));
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  const handleSelect = (id: ChatEndpoint) => {
    onSelect(id);
    setIsOpen(false);
    setSearchQuery('');
  };
  return <div className="relative inline-block text-left" data-id="element-1801">
      <button ref={buttonRef} onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600 text-xs font-medium text-slate-300 transition-all duration-200" data-id="element-1802">
        <AgentIcon name={selectedAgent.icon} size={14} className={selectedAgent.textClass} data-id="element-1803" />

        <span data-id="element-1804">{selectedAgent.shortName}</span>
        <ChevronDown size={14} className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1805" />
      </button>

      {isOpen && <div ref={dropdownRef} className="absolute bottom-full left-0 mb-2 w-72 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-200" data-id="element-1806">
          <div className="p-2 border-b border-slate-800" data-id="element-1807">
            <div className="relative" data-id="element-1808">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-1809" />

              <input type="text" placeholder="Search models..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50" autoFocus data-id="element-1810" />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto py-1 scrollbar-thin scrollbar-thumb-slate-700" data-id="element-1811">
            {filteredAgents.length > 0 ? filteredAgents.map(agent => <button key={agent.id} onClick={() => handleSelect(agent.id)} className={`w-full flex items-start gap-3 px-3 py-2 text-left transition-colors ${selectedAgentId === agent.id ? 'bg-indigo-500/10' : 'hover:bg-slate-800/50'} ${agent.status === 'deprecated' ? 'opacity-60' : ''}`} data-id="element-1812">
                  <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center" data-id="element-1813">
                    <AgentIcon name={agent.icon} size={14} className={agent.textClass} data-id="element-1814" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1815">
                    <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-1816">
                      <div className="flex items-center gap-1.5" data-id="element-1817">
                        <span className={`text-sm font-medium ${selectedAgentId === agent.id ? 'text-indigo-400' : 'text-slate-200'}`} data-id="element-1818">
                          {agent.name}
                        </span>
                        {agent.recommended && <Star size={10} className="text-amber-400 fill-amber-400/20" data-id="element-1819" />}
                      </div>
                      {selectedAgentId === agent.id && <Check size={14} className="text-indigo-500 flex-shrink-0" data-id="element-1820" />}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-tight line-clamp-2" data-id="element-1821">
                      {agent.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5" data-id="element-1822">
                      <span className="text-[9px] font-mono text-slate-600 bg-slate-950 px-1.5 py-0.5 rounded border border-slate-800" data-id="element-1823">
                        {agent.latencyHint}
                      </span>
                      {agent.badge && <span className={`text-[9px] px-1.5 py-0.5 rounded border ${agent.borderClass}`} data-id="element-1824">
                          {agent.badge}
                        </span>}
                    </div>
                  </div>
                </button>) : <div className="px-4 py-6 text-center text-xs text-slate-500" data-id="element-1825">
                No models found matching "{searchQuery}"
              </div>}
          </div>
        </div>}
    </div>;
}